import React, { useCallback } from "react";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";

import ProductCategoryCard from "components/botanic/ProductCategoryCard";
import ProductCategoryCardPlaceholder from "components/botanic/ProductCategoryCard/ProductCategoryCardPlaceholder";

type Props = {
  className?: string;
  isLoading?: boolean;
  coins: string[];
  hrefLink?: string;
  hrefLinkQueryParam?: string;
  hrefProductCatQueryKey?: string;
  title?: string;
  trackEvent?: {
    eventAction?: string;
    eventCategory?: string;
  };
  filterable?: boolean;
};

const CoinList: React.FC<Props> = ({
  className,
  coins,
  isLoading = false,
  hrefLink = "/search/shop",
  hrefLinkQueryParam = "",
  hrefProductCatQueryKey = "product_category",
  title,
  trackEvent,
  filterable,
}) => {
  const { toggleFilter } = useFilters({ shallowRouting: true });
  const onFilterClick = useCallback(
    (filterOption: string) => {
      toggleFilter({ [`product_category[]`]: filterOption });
    },
    [toggleFilter]
  );

  const { publishEvent } = useEventTracker();

  const publishEventAction = trackEvent?.eventAction || Action.click;
  const publishEventCategory =
    trackEvent?.eventCategory || Category.productCategoryCoinFilter;

  const coinCategories = Object.keys(CATEGORY_NAMES).filter((category) =>
    coins.includes(category)
  );

  return isLoading || coinCategories.length ? (
    <div className={className}>
      {title && <h2 className="heading--m font-bold">{title}</h2>}
      <div className="-mx-sm p-sm pb-md overflow-x-scroll lg:overflow-x-visible">
        {coinCategories.length ? (
          <ul
            data-testid="coin-list"
            className="inline-flex lg:flex -mr-sm lg:mr-none"
          >
            {coinCategories.map((category) => (
              <li
                key={category}
                className="mx-sm flex-shrink-0"
                style={{ minWidth: 80, width: 80 }}
              >
                <ProductCategoryCard
                  image={`https://leafly-public.imgix.net/web-dispensary/category-images/${category.toLowerCase()}_300.png`}
                  title={CATEGORY_NAMES[category]}
                  titleSize="xs"
                  titleClassName="underline"
                  url={`${hrefLink}?${hrefProductCatQueryKey}=${category}${hrefLinkQueryParam}`}
                  disableLazyLoad={true}
                  sizes={[80]}
                  onClick={() => {
                    publishEvent({
                      action: publishEventAction,
                      category: publishEventCategory,
                      label: CATEGORY_NAMES[category]
                        .toLowerCase()
                        .replace(/-/g, ""),
                    });
                    filterable && onFilterClick(category);
                  }}
                  filterable={filterable}
                />
              </li>
            ))}
          </ul>
        ) : (
          !coinCategories.length &&
          isLoading && (
            <div className="flex">
              {Array(10)
                .fill(null)
                .map((_, i) => (
                  <ProductCategoryCardPlaceholder
                    key={`coin-list-${i}`}
                    className="mx-sm"
                    style={{ minWidth: 80, width: 80 }}
                  />
                ))}
            </div>
          )
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};

export default CoinList;
